import React, { useEffect, useState } from 'react';
import { User, Phone, Mail, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { StoreConfigService } from '@/services/store/storeConfig';

// Definição das interfaces de props necessárias
interface ClientInfo {
  phoneNumber: string;
  email: string;
  hasWhatsApp: boolean;
  whatsappMessage: string;
}

interface UnlockedOrderInfo {
  isExclusive: boolean;
  unlockedAt: string;
}

interface OrderInfo {
  unlockedCount: number;
}

interface ClientContactInfoProps {
  customerName: string;
  clientInfo?: ClientInfo;
  unlockedOrderInfo: UnlockedOrderInfo;
  order: OrderInfo;
}

// Componente funcional
export const ClientContactInfo: React.FC<ClientContactInfoProps> = ({
  customerName,
  clientInfo,
  unlockedOrderInfo,
  order,
}) => {
  const [testLink, setTestLink] = useState('');

  useEffect(() => {
    const fetchStoreConfig = async () => {
      try {
        const res = await StoreConfigService.getStoreConfig();
        setTestLink(res);
      } catch {
        setTestLink('');
      }
    };

    fetchStoreConfig();
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        marginTop: '1.4rem',
      }}
    >
      {/* Contact Information */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          padding: '24px',
          backgroundColor: '#F6F6F6',
          borderRadius: '16px',
        }}
      >
        {/* Customer Name */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <User
            style={{
              width: '1rem',
              height: '1rem',
              color: '#6b7280', // text-gray-500
            }}
          />
          <span
            style={{
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--foreground)',
            }}
          >
            {customerName}
          </span>
        </div>

        {/* Phone Number */}
        {clientInfo?.phoneNumber && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <Phone
              style={{
                width: '1rem',
                height: '1rem',
                color: '#6b7280', // text-gray-500
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-weight-normal)',
                color: 'var(--foreground)',
              }}
            >
              {clientInfo.phoneNumber?.replace(
                /(\d{2})(\d{5})(\d{4})/,
                '($1) $2-$3'
              )}
            </span>
          </div>
        )}

        {/* Email */}
        {clientInfo?.email && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <Mail
              style={{
                width: '1rem',
                height: '1rem',
                color: '#6b7280', // text-gray-500
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-weight-normal)',
                color: 'var(--foreground)',
              }}
            >
              {clientInfo.email}
            </span>
          </div>
        )}

        {/* Unlocked Date Caption */}
        {/*<div
          style={{
            paddingTop: '8px',
            marginTop: '8px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0px',
          }}
        >
          <span
            style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-normal)',
              color: 'var(--muted-foreground)',
            }}
          >
            {unlockedOrderInfo.isExclusive
              ? 'Este pedido é exclusivo seu.'
              : order.unlockedCount === 1
                ? 'Apenas você liberou até agora.'
                : `Você e mais ${order.unlockedCount - 1} liberaram este pedido.`}
          </span>
          <span
            style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-normal)',
              color: 'var(--muted-foreground)',
            }}
          >
            Liberado em{' '}
            {new Date(unlockedOrderInfo.unlockedAt).toLocaleDateString(
              'pt-BR',
              {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              }
            )}{' '}
            às{' '}
            {new Date(unlockedOrderInfo.unlockedAt).toLocaleTimeString(
              'pt-BR',
              {
                hour: '2-digit',
                minute: '2-digit',
              }
            )}
          </span>
        </div>*/}
      </div>

      {/* Action Buttons */}
      <div
        style={{
          display: 'flex',
          gap: '0.75rem', // gap-3
          flexDirection: 'column',
        }}
      >
        {/* Call Button */}
        {clientInfo?.phoneNumber && (
          <motion.a
            href={`contato?action=whatsapp&phone=55${clientInfo.phoneNumber.replace(/\D/g, '')}&text=${clientInfo?.whatsappMessage}`}
            style={{
              flex: '1 1 0%', // flex-1
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem', // gap-2
              transition: 'all 150ms ease-in-out', // transition-all (aproximação)
              minHeight: '48px',
              paddingTop: '12px',
              paddingBottom: '12px',
              paddingLeft: '16px',
              paddingRight: '16px',
              borderRadius: 'var(--radius-button)',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--background)',
              color: 'var(--foreground)',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-weight-semibold)',
              textDecoration: 'none',
            }}
            whileTap={{ scale: 0.95 }}
          >
            <Phone
              style={{
                width: '1.25rem', // w-5
                height: '1.25rem', // h-5
              }}
            />
            Ligar
          </motion.a>
        )}

        {/* WhatsApp Button */}
        {clientInfo?.phoneNumber && clientInfo?.hasWhatsApp && (
          <motion.a
            href={`contato?action=whatsapp&phone=55${clientInfo.phoneNumber.replace(/\D/g, '')}&text=${clientInfo?.whatsappMessage}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flex: '1 1 0%', // flex-1
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem', // gap-2
              transition: 'all 150ms ease-in-out', // transition-all (aproximação)
              minHeight: '48px',
              paddingTop: '12px',
              paddingBottom: '12px',
              paddingLeft: '16px',
              paddingRight: '16px',
              borderRadius: 'var(--radius-button)',
              border: 'none',
              backgroundColor: '#25D366',
              color: '#ffffff',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-weight-semibold)',
              textDecoration: 'none',
            }}
            whileTap={{ scale: 0.95 }}
          >
            <MessageCircle
              style={{
                width: '1.25rem', // w-5
                height: '1.25rem', // h-5
              }}
            />
            WhatsApp
          </motion.a>
        )}
      </div>
    </div>
  );
};
